// Repair model-written markdown tables whose header and delimiter rows disagree
// on how many cells they have, so markdown-it renders a table instead of
// refusing to.
//
// Why this exists. PROD message -P0-01gBLCY7mA0jyJuj wrote a two-cell header
// (`| Preterite | Imperfect |`) above a three-cell delimiter (`|---|---|---|`).
// markdown-it's table rule (lib/rules_block/table.mjs) requires those two counts
// to be equal and bails out entirely when they are not, so all four data rows
// fell through to a single paragraph of literal pipe characters -- and with
// `typographer: true` each `---` cell came out as an em dash. The learner read
// that as a truncated table; nothing was truncated. Running this repair at
// render time rather than before storage is what fixes the messages already in
// RTDB: no data migration, an updated client simply starts drawing the table.
//
// Two rules govern the repair, and the second one is the reason this is not just
// "trim the delimiter to the header's width":
//
//   1. When the data rows agree with the header, the delimiter row is conformed
//      to the header's cell count, keeping each cell's alignment marker.
//   2. When some data row carries MORE cells than the header, the header and the
//      delimiter are padded out to that width instead. markdown-it truncates
//      every data row to the header's column count (its data-row loop runs
//      `for i < columnCount`), so conforming to an under-counted header would
//      make it silently drop a column of real content -- a worse failure than
//      the visible one it replaces, and the reason the design spec rejected the
//      always-conform-to-the-header variant.
//
// Nothing else in the message is touched. A block that markdown-it already
// accepts, and whose data rows are no wider than its header, is returned
// byte-identical.
//
// This is a line scanner, not a container parser, so it is deliberately
// conservative on both ends of a block. It never treats a line that opens a
// container -- a list bullet, an ATX heading, a blockquote marker -- as a table
// header, because markdown-it strips such a marker before the table begins and
// this scanner would count it as a cell. And it ends a block at a blank line, at
// four-column indentation, at a code fence (including one opened on a list-item
// line), at a thematic break, at a container marker, at a line outdented past
// the block's own indentation, and at the start of a following header +
// delimiter pair. Each of those is a place markdown-it's own rules would end the
// table, and stopping early only ever means a block is left alone.
//
// Tables inside blockquotes are therefore never repaired: every one of their
// lines starts with ">". That is a known limit, not an oversight -- rewriting
// lines inside a container prefix needs a real container parser.

// A cell in the delimiter row, exactly as markdown-it validates it.
const DELIMITER_CELL_PATTERN = /^:?-+:?$/;

// An opening or closing code fence: three or more backticks or tildes.
const CODE_FENCE_PATTERN = /^(`{3,}|~{3,})(.*)$/;

// A list marker at the start of a line. A fence may open on the same line as
// one (`- ```js`), in which case the fence is what the rest of the line is.
const LIST_MARKER_PATTERN = /^(?:[-*+]|\d{1,9}[.)])\s+/;

// Block starters that end a table's run of data rows. markdown-it stops its
// data-row loop on the "blockquote" terminator rules, which is what these are:
// a blockquote marker, an ATX heading, a list bullet, an ordered-list marker.
const BLOCK_INTERRUPT_PATTERN =
  /^(?:>|#{1,6}(?:\s|$)|[-*+](?:\s|$)|\d{1,9}[.)](?:\s|$))/;

// A thematic break, which also ends the run. Written without pipes on purpose:
// a delimiter row such as `---|---` is not a thematic break.
const THEMATIC_BREAK_PATTERN = /^(?:(?:\*\s*){3,}|(?:-\s*){3,}|(?:_\s*){3,})$/;

// A repair only ever appends empty cells, so the loop that does it needs a
// bound rather than a proof; this one is far above any real table's width.
const MAX_PAD_ITERATIONS = 64;

/**
 * How far a line is indented, in columns, with a tab counted as four.
 * markdown-it treats four or more columns as an indented code block and its
 * table rule refuses such a line, so this normalizer refuses it too.
 */
function indentColumns(line) {
  let columns = 0;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === " ") columns += 1;
    else if (ch === "\t") columns += 4;
    else break;
  }
  return columns;
}

/**
 * Split a table row into its cells the way markdown-it's table rule does.
 *
 * This is a deliberate port of that rule's `escapedSplit` helper plus the
 * shift/pop that follows it, and it is a port rather than an independent
 * implementation for one reason: this normalizer's idea of "how many cells does
 * this row have" must never disagree with the parser it is repairing, or the
 * repair produces markdown that still does not parse.
 *
 * Two consequences of matching markdown-it exactly are worth stating:
 *  - `\|` is content, not a cell boundary, so `| a \| b | c |` is two cells.
 *  - a pipe inside a backtick code span IS a cell boundary. That is markdown-it
 *    14.1.0's behavior (verified by rendering `| `a | b` | c |` above a
 *    three-cell delimiter, which produces three columns) and it is what the GFM
 *    tables extension specifies: a pipe inside a table cell has to be escaped
 *    even when it sits inside a code span. Counting code spans as content here
 *    would make every repair involving one either fail to parse or pad too
 *    narrowly and let markdown-it truncate a column away.
 *  - both boundary-pipe styles land on the same count, because the leading and
 *    trailing empty cells are dropped: `| a | b |` and `a | b` are both two.
 */
export function splitRowCells(trimmedLine) {
  const cells = [];
  let carried = "";
  let lastPos = 0;
  let isEscaped = false;

  for (let pos = 0; pos < trimmedLine.length; pos++) {
    const ch = trimmedLine[pos];
    if (ch === "|") {
      if (!isEscaped) {
        cells.push(carried + trimmedLine.slice(lastPos, pos));
        carried = "";
        lastPos = pos + 1;
      } else {
        // `\|`: drop the backslash, keep the pipe as content.
        carried += trimmedLine.slice(lastPos, pos - 1);
        lastPos = pos;
      }
    }
    isEscaped = ch === "\\";
  }
  cells.push(carried + trimmedLine.slice(lastPos));

  if (cells.length && cells[0] === "") cells.shift();
  if (cells.length && cells[cells.length - 1] === "") cells.pop();
  return cells;
}

/**
 * Parse a delimiter row, returning its alignment cells (`---`, `:---`, `---:`,
 * `:---:`) in order, or null when the line is not one.
 *
 * Every check here mirrors markdown-it's, including its plain `split("|")` (the
 * delimiter row is not escape-aware there) and its rule that an empty cell is
 * tolerated only at the very start or end of the line.
 *
 * One check is stricter than markdown-it's: the line must contain a pipe. A
 * pipe-less `---` under a line that happens to contain a pipe is a setext
 * heading underline or a thematic break today, and treating it as a
 * single-column delimiter row would turn an `<h2>` into a table.
 */
export function parseDelimiterRow(trimmedLine) {
  if (trimmedLine.length < 2) return null;
  if (trimmedLine.indexOf("|") === -1) return null;

  const first = trimmedLine[0];
  if (first !== "|" && first !== "-" && first !== ":") return null;

  const second = trimmedLine[1];
  const secondIsSpace = /\s/.test(second);
  if (second !== "|" && second !== "-" && second !== ":" && !secondIsSpace) {
    return null;
  }
  // markdown-it: a leading "-" followed by a space is ambiguous with a list.
  if (first === "-" && secondIsSpace) return null;

  for (let i = 0; i < trimmedLine.length; i++) {
    const ch = trimmedLine[i];
    if (ch !== "|" && ch !== "-" && ch !== ":" && !/\s/.test(ch)) return null;
  }

  const raw = trimmedLine.split("|");
  const aligns = [];
  for (let i = 0; i < raw.length; i++) {
    const cell = raw[i].trim();
    if (!cell) {
      // Empty is allowed only as the boundary pipe's own side.
      if (i === 0 || i === raw.length - 1) continue;
      return null;
    }
    if (!DELIMITER_CELL_PATTERN.test(cell)) return null;
    aligns.push(cell);
  }
  return aligns.length ? aligns : null;
}

/** True when the trimmed line opens or closes a fenced code block. */
function fenceMarkerOf(trimmedLine) {
  const match = CODE_FENCE_PATTERN.exec(trimmedLine);
  if (!match) return null;
  const marker = match[1];
  const info = match[2];
  // An info string on a backtick fence may not itself contain a backtick.
  if (marker[0] === "`" && info.indexOf("`") !== -1) return null;
  return { char: marker[0], length: marker.length, info: info.trim() };
}

/**
 * The fence a line opens, counting a fence that opens on the same line as a list
 * marker. Skipping that form would let this normalizer rewrite the contents of a
 * code block, which it must never do.
 */
function openingFenceOf(trimmedLine) {
  return (
    fenceMarkerOf(trimmedLine) ||
    fenceMarkerOf(trimmedLine.replace(LIST_MARKER_PATTERN, ""))
  );
}

/** Append empty cells until the row splits into `target` cells. */
function padRowToCellCount(trimmedLine, target) {
  let padded = trimmedLine;
  for (
    let i = 0;
    i < MAX_PAD_ITERATIONS && splitRowCells(padded).length < target;
    i++
  ) {
    padded += " |";
  }
  return padded;
}

/**
 * Build a delimiter row of `target` cells, reusing the original row's alignment
 * markers positionally and defaulting any cell beyond them to `---`. The
 * original row's boundary-pipe style is preserved so the repair looks like the
 * markdown around it.
 */
function buildDelimiterRow(originalTrimmed, aligns, target) {
  const cells = [];
  for (let i = 0; i < target; i++) cells.push(aligns[i] || "---");
  const leading = originalTrimmed.startsWith("|") ? "|" : "";
  const trailing = originalTrimmed.endsWith("|") ? "|" : "";
  return leading + cells.join("|") + trailing;
}

/**
 * A blank line matching a neighbouring line's line ending, so inserting one into
 * CRLF text does not leave a bare LF among CRLFs.
 */
function separatorLine(neighbour) {
  return typeof neighbour === "string" && neighbour.endsWith("\r") ? "\r" : "";
}

/** Replace a line's content while keeping its leading and trailing whitespace. */
function rewriteLine(original, newTrimmed) {
  const leading = original.slice(0, original.length - original.trimStart().length);
  const trailing = original.slice(original.trimEnd().length);
  return leading + newTrimmed + trailing;
}

/**
 * Repair every table block in `text` whose header and delimiter rows disagree on
 * cell count, and every block whose data rows are wider than its header.
 * Everything else -- fenced code blocks, indented code, prose, and any table
 * markdown-it already renders correctly -- comes back byte-identical.
 */
export function normalizeMarkdownTables(text) {
  if (typeof text !== "string") return text;
  // Early-out on the overwhelmingly common case: no pipe means no table.
  if (text.indexOf("|") === -1) return text;

  const lines = text.split("\n");
  let changed = false;
  let openFence = null;
  // True when the block about to be examined is pressed directly against a table
  // block that has just ended, with no blank line between them.
  let pressedAgainstPreviousBlock = false;

  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();
    const indented = indentColumns(lines[i]) >= 4;
    const precededByTableBlock = pressedAgainstPreviousBlock;
    pressedAgainstPreviousBlock = false;

    // Fence tracking ignores indentation on purpose. A fence opened four or more
    // columns in -- two for a list container plus the two a fence may itself be
    // indented -- still fences the lines under it, and those lines can be
    // indented less than the fence is. Missing that would let this normalizer
    // rewrite code-block content, which it must never do; over-reading a fence
    // only ever means a block goes unrepaired.
    if (openFence) {
      const closing = fenceMarkerOf(trimmed);
      if (
        closing &&
        closing.char === openFence.char &&
        closing.length >= openFence.length &&
        closing.info === ""
      ) {
        openFence = null;
      }
      continue;
    }

    const opening = openingFenceOf(trimmed);
    if (opening) {
      openFence = opening;
      continue;
    }

    if (indented) continue;
    if (trimmed.indexOf("|") === -1) continue;
    // A line that opens a block container -- a list bullet, an ATX heading, a
    // blockquote marker -- is never treated as a table header. markdown-it
    // strips the container marker before the table starts (verified: "- | A | B |"
    // above an indented two-cell delimiter renders as a two-column table inside a
    // list item), while this line scanner would count the marker as a cell and
    // widen the delimiter to match, breaking a table that renders correctly
    // today. Refusing to repair such a block leaves it exactly as it is.
    if (BLOCK_INTERRUPT_PATTERN.test(trimmed)) continue;
    if (i + 1 >= lines.length) continue;
    if (indentColumns(lines[i + 1]) >= 4) continue;

    const aligns = parseDelimiterRow(lines[i + 1].trim());
    if (!aligns) continue;

    // A header + delimiter pair: this is a table block.
    const headerCells = splitRowCells(trimmed);
    if (headerCells.length === 0) continue;

    // Walk the data rows this block owns. A row wider than the header has to
    // widen the table, because markdown-it would otherwise drop its extra cells.
    //
    // The outdent baseline is the SHALLOWER of the header and delimiter rows. A
    // header indented one to three columns above a flush-left delimiter is still
    // one table to markdown-it, and taking the header's own indentation as the
    // baseline would end the scan at the first data row and let the repair
    // narrow the table onto a header that under-counts it.
    const blockIndent = Math.min(
      indentColumns(lines[i]),
      indentColumns(lines[i + 1])
    );
    let end = i + 2;
    let widestDataRow = 0;
    let nextBlockFollows = false;
    for (; end < lines.length; end++) {
      const rowTrimmed = lines[end].trim();
      if (!rowTrimmed) break;
      if (indentColumns(lines[end]) >= 4) break;
      // Outdented past the block means the line left this table's container -- a
      // table nested in a list item must not be widened by the text that follows
      // the list.
      if (indentColumns(lines[end]) < blockIndent) break;
      if (openingFenceOf(rowTrimmed)) break;
      if (THEMATIC_BREAK_PATTERN.test(rowTrimmed)) break;
      if (BLOCK_INTERRUPT_PATTERN.test(rowTrimmed)) break;
      // A header + delimiter pair starts a NEW table block -- but never at the
      // very first data row, where "a new table with no rows above it" and "this
      // table's first data row followed by a dashes-only row" are the same two
      // lines. Reading them as data keeps the repaired table from coming out
      // empty, and matches what markdown-it renders once the repair lands.
      if (
        end > i + 2 &&
        rowTrimmed.indexOf("|") !== -1 &&
        end + 1 < lines.length &&
        indentColumns(lines[end + 1]) < 4 &&
        parseDelimiterRow(lines[end + 1].trim())
      ) {
        nextBlockFollows = true;
        break;
      }
      if (rowTrimmed.indexOf("|") === -1) continue;
      widestDataRow = Math.max(widestDataRow, splitRowCells(rowTrimmed).length);
    }

    const alreadyRenders =
      headerCells.length === aligns.length &&
      widestDataRow <= headerCells.length;
    if (alreadyRenders) {
      pressedAgainstPreviousBlock = nextBlockFollows;
      i = end - 1;
      continue;
    }

    const target = Math.max(headerCells.length, widestDataRow);
    const newHeaderTrimmed =
      target > headerCells.length ? padRowToCellCount(trimmed, target) : trimmed;
    const newDelimiterTrimmed = buildDelimiterRow(
      lines[i + 1].trim(),
      aligns,
      target
    );

    // Fail closed: if the rewrite did not actually land on a parseable pair,
    // leave the block exactly as the model wrote it rather than emit something
    // different and equally broken.
    const rewrittenAligns = parseDelimiterRow(newDelimiterTrimmed);
    if (
      !rewrittenAligns ||
      rewrittenAligns.length !== target ||
      splitRowCells(newHeaderTrimmed).length !== target
    ) {
      pressedAgainstPreviousBlock = nextBlockFollows;
      i = end - 1;
      continue;
    }

    let repaired = false;
    if (newHeaderTrimmed !== trimmed) {
      lines[i] = rewriteLine(lines[i], newHeaderTrimmed);
      repaired = true;
    }
    if (newDelimiterTrimmed !== lines[i + 1].trim()) {
      lines[i + 1] = rewriteLine(lines[i + 1], newDelimiterTrimmed);
      repaired = true;
    }
    changed = changed || repaired;

    // markdown-it's table rule is not one of its own table terminators, so two
    // table blocks pressed together with no blank line between them render as
    // ONE table: the second block's header and delimiter become body rows, and
    // the delimiter comes out as a row of em dashes. While a block is broken
    // that does not happen -- a broken block degrades to a paragraph, which a
    // table IS allowed to interrupt -- so it is repairing a block that presses
    // it against its neighbour. Insert one blank line on whichever side this
    // repair created, and only there: two blocks that both render correctly
    // today are left exactly as they are.
    let nextStart = end;
    if (repaired && precededByTableBlock) {
      lines.splice(i, 0, separatorLine(lines[i]));
      nextStart += 1;
      changed = true;
    }
    if (repaired && nextBlockFollows) {
      lines.splice(nextStart, 0, separatorLine(lines[nextStart]));
      changed = true;
      i = nextStart;
      continue;
    }
    pressedAgainstPreviousBlock = nextBlockFollows;
    i = nextStart - 1;
  }

  return changed ? lines.join("\n") : text;
}
